/**
 * /well-servicing/jobs/new — open a job (POST /well-servicing/jobs,
 * fieldiq-api#1282). A full page, not a modal. `?wellId=` starts the form on
 * that well. On save the job's own page replaces this one.
 *
 * WS_DISPATCH (admin, office staff, supervisor, dispatcher) only; any other
 * role is told so here rather than handed a form the API would refuse.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useWsMeta, useWsUsers, useWsWells } from '../../hooks/useWellServicing';
import { useCreateWsJob } from '../../hooks/useWsJobs';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canDispatchWs, wsJobRefusal } from '../../lib/well-servicing';
import { useBackToList } from '../downhole/recordPage';
import { JobForm } from './JobForm';
import { WS_EYEBROW, WsEmpty, WsLoading, WsPanel } from './WsBits';

export default function WsNewJobScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const back = useBackToList('/well-servicing/jobs');
  const { user } = useAuth();
  const canAdd = canDispatchWs(user?.role);
  const meta = useWsMeta();
  const wells = useWsWells();
  const users = useWsUsers(canAdd);
  const create = useCreateWsJob();
  const [error, setError] = useState<string | null>(null);
  const metaFailure = queryLoadFailure(meta);
  const wellsFailure = queryLoadFailure(wells);

  return (
    <div data-testid="ws-new-job-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={WS_EYEBROW} title="New job" description="Draft it now, or schedule it so the crew can open tour sheets." onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WsPanel eyebrow="Job register" title="Job">
            {!canAdd ? (
              <WsEmpty>Only an admin, office staff, a supervisor or a dispatcher can open a job.</WsEmpty>
            ) : meta.isLoading || wells.isLoading ? (
              <WsLoading label="Loading form" />
            ) : metaFailure || wellsFailure ? (
              <LoadFailureNotice
                error={queryFailureError(metaFailure ? meta : wells)}
                kind={(metaFailure ?? wellsFailure) || 'unreachable'}
                subject={metaFailure ? 'the job form' : 'your wells'}
              />
            ) : (wells.data?.data ?? []).length === 0 ? (
              <WsEmpty>A job is on a well, and there are no wells yet. Add one under Wells first.</WsEmpty>
            ) : (
              <JobForm
                job={null}
                initialWellId={searchParams.get('wellId') ?? undefined}
                catalog={meta.data?.data ?? null}
                meta={meta.data?.meta ?? null}
                users={users.data ?? []}
                pending={create.isPending}
                error={error}
                submitLabel="Create job"
                onCancel={back}
                onSubmit={(body) => {
                  setError(null);
                  create.mutate(body, {
                    onSuccess: (res) => {
                      toast.success(res.data?.jobNumber ? `Job ${res.data.jobNumber} created` : 'Job created');
                      navigate(res.data?.id ? `/well-servicing/jobs/${res.data.id}` : '/well-servicing/jobs', { replace: true });
                    },
                    onError: (err) => setError(wsJobRefusal(err, 'The job could not be created.')),
                  });
                }}
              />
            )}
          </WsPanel>
        </div>
      </section>
    </div>
  );
}
